import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DownloadWarningModal from "../components/DownloadWarningModal";
import InteractiveTour from "@/components/InteractiveTour";

// Helper function to group entries by quarter
const groupByQuarter = (entries) => {
  const quarters = {};
  entries.forEach(entry => {
    const date = new Date(entry.date);
    const year = date.getFullYear();
    const quarter = Math.floor((date.getMonth()) / 3) + 1;
    const key = `${year}-Q${quarter}`;
    
    if (!quarters[key]) {
      quarters[key] = [];
    }
    quarters[key].push(entry);
  });
  
  // Sort quarters descending (newest first)
  return Object.keys(quarters)
    .sort((a, b) => b.localeCompare(a))
    .map(key => ({
      quarter: key,
      entries: quarters[key].sort((a, b) => new Date(b.date) - new Date(a.date))
    }));
};

export default function Eingangsliste() {
  const [activeTab, setActiveTab] = useState("geschäftlich");
  const [entries, setEntries] = useState([]);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    article: "",
    quantity: "1",
    price_category: "tp",
    ust_percent: "19",
    type: "geschäftlich"
  });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDownloadWarning, setShowDownloadWarning] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);
  const [showTourManual, setShowTourManual] = useState(false);

  useEffect(() => {
    loadEntries();
    loadProducts();
  }, [activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setShowForm(false);
    setFormData({
      ...formData,
      type: tab
    });
  };

  const loadEntries = async () => {
    try {
      const typeParam = activeTab === "gesamt" ? "" : `?type=${encodeURIComponent(activeTab)}`;
      const response = await axios.get(`${API}/incoming${typeParam}`);
      setEntries(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden");
    }
  };

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Fehler beim Laden der Produkte");
    }
  };

  const handleProductSelect = (productName) => {
    const product = products.find(p => p.name === productName);
    if (product) {
      setSelectedProduct(product);
      setFormData({
        ...formData,
        article: product.name,
        ust_percent: product.ust_percent.toString()
      });
    }
  };

  const getPriceForCategory = () => {
    if (!selectedProduct) return 0;
    const price = selectedProduct[formData.price_category] || selectedProduct.tp || selectedProduct.kunde || 0;
    return price;
  };

  const getTotalPrice = () => {
    const quantity = parseInt(formData.quantity) || 0;
    const price = getPriceForCategory();
    return quantity * price;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/incoming`, {
        ...formData,
        quantity: parseInt(formData.quantity),
        ust_percent: parseFloat(formData.ust_percent)
      });
      
      toast.success("Wareneingang erfasst");
      setFormData({
        date: new Date().toISOString().split('T')[0],
        article: "",
        quantity: "1",
        price_category: "tp",
        ust_percent: "19",
        type: activeTab
      });
      setSelectedProduct(null);
      setShowForm(false);
      loadEntries();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/incoming/${id}`);
      toast.success("Eintrag gelöscht");
      loadEntries();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const requestDownloadPDF = (quarter) => {
    setPendingDownload(quarter);
    setShowDownloadWarning(true);
  };

  const confirmDownload = async () => {
    if (!pendingDownload) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/incoming/export/${pendingDownload}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Eingangsliste_${pendingDownload}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF wird heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim Herunterladen");
    } finally {
      setShowDownloadWarning(false);
      setPendingDownload(null);
    }
  };

  const groupedEntries = groupByQuarter(entries);

  const priceCategories = [
    { value: "kunde", label: "Kunde" },
    { value: "tp", label: "TP" },
    { value: "ba", label: "BA" },
    { value: "five_plus_one", label: "5+1" },
    { value: "ap", label: "AP" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Link to="/dashboard" data-tour="zurueck-button">
          <Button variant="outline" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Zurück zu Inventory
          </Button>
        </Link>

        <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-slate-800">Eingangsliste</h1>
            <p className="text-slate-500 mt-1">Wareneingang erfassen (erhöht Bestand)</p>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 border-b border-slate-200 overflow-x-auto" data-tour="filter-tabs">
            <button
              onClick={() => handleTabChange("geschäftlich")}
              className={`px-4 sm:px-6 py-3 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
                activeTab === "geschäftlich"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Geschäftlich
            </button>
            <button
              onClick={() => handleTabChange("haushalt")}
              className={`px-4 sm:px-6 py-3 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
                activeTab === "haushalt"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Haushalt
            </button>
            <button
              onClick={() => handleTabChange("gesamt")}
              className={`px-4 sm:px-6 py-3 font-semibold transition-colors whitespace-nowrap text-sm sm:text-base ${
                activeTab === "gesamt"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Gesamt
            </button>
          </div>

          {activeTab !== "gesamt" && (
            <Button 
              onClick={() => setShowForm(!showForm)}
              className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 mb-4"
              data-tour="eingang-hinzufuegen-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Eingang hinzufügen
            </Button>
          )}

          {showForm && (
            <form onSubmit={handleSubmit} className="mb-8 p-6 bg-slate-50 rounded-xl border border-slate-200" data-tour="eingang-formular">
              <h3 className="text-lg font-semibold mb-4">Neuer Wareneingang</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="date">Datum *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="article">Artikel *</Label>
                  <Select onValueChange={handleProductSelect} value={formData.article}>
                    <SelectTrigger>
                      <SelectValue placeholder="Artikel wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((product) => (
                        <SelectItem key={product.id} value={product.name}>
                          {product.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="price_category">Preiskategorie *</Label>
                  <Select 
                    onValueChange={(value) => setFormData({...formData, price_category: value})}
                    value={formData.price_category}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {priceCategories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="quantity">Menge *</Label>
                  <Input
                    id="quantity"
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                    required
                    placeholder="10"
                  />
                </div>
                <div>
                  <Label>Preis (wird automatisch gesetzt)</Label>
                  <div className="text-lg font-semibold text-emerald-600 mt-2">
                    {getPriceForCategory().toFixed(2)} € 
                    {selectedProduct && selectedProduct.punkte > 0 && (
                      <span className="text-sm text-slate-500 ml-2">
                        (+{selectedProduct.punkte} Pkt/Stk)
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Gesamtpreis</Label>
                  <div className="text-xl font-bold text-blue-600 mt-2">
                    {getTotalPrice().toFixed(2)} €
                  </div>
                </div>
                <div>
                  <Label htmlFor="ust_percent">USt. % *</Label>
                  <Input
                    id="ust_percent"
                    type="number"
                    step="0.01"
                    value={formData.ust_percent}
                    onChange={(e) => setFormData({...formData, ust_percent: e.target.value})}
                    required
                    placeholder="19"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  Speichern
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowForm(false)}
                >
                  Abbrechen
                </Button>
              </div>
            </form>
          )}

          <div className="space-y-8">
            {groupedEntries.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                Noch keine Eingänge erfasst
              </div>
            )}
            
            {groupedEntries.map((quarterGroup, index) => (
              <div key={quarterGroup.quarter} className="bg-slate-50 rounded-lg border border-slate-200" data-tour={index === 0 ? "quartal-eintrag" : undefined}>
                <div className="flex justify-between items-center p-4 bg-slate-100 rounded-t-lg border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800">
                    {quarterGroup.quarter.replace('Q', '. Quartal ')}
                  </h3>
                  {activeTab === "geschäftlich" && (
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={() => requestDownloadPDF(quarterGroup.quarter)}
                      className="bg-white hover:bg-slate-50"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      PDF Export
                    </Button>
                  )}
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Datum</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Artikel</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-600">Kategorie</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Menge</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Preis</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Gesamt</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Punkte</th>
                        <th className="text-right py-3 px-4 font-semibold text-slate-600">Aktion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quarterGroup.entries.map((entry) => (
                        <tr key={entry.id} className="border-b border-slate-100 hover:bg-white">
                          <td className="py-3 px-4">{new Date(entry.date).toLocaleDateString('de-DE')}</td>
                          <td className="py-3 px-4">{entry.article}</td>
                          <td className="py-3 px-4 text-sm text-slate-500">
                            {priceCategories.find(c => c.value === entry.price_category)?.label || entry.price_category}
                          </td>
                          <td className="py-3 px-4 text-right">{entry.quantity}</td>
                          <td className="py-3 px-4 text-right">{entry.actual_price.toFixed(2)} €</td>
                          <td className="py-3 px-4 text-right font-semibold">{entry.vk_total.toFixed(2)} €</td>
                          <td className="py-3 px-4 text-right text-emerald-600">
                            {entry.punkte_earned > 0 ? entry.punkte_earned : '-'}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handleDelete(entry.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                <div className="p-4 bg-slate-50 rounded-b-lg border-t border-slate-200">
                  <div className="text-sm text-slate-600 flex flex-wrap gap-4">
                    <div>
                      <strong>Quartal Zusammenfassung:</strong> {quarterGroup.entries.length} Einträge
                    </div>
                    <div>
                      Gesamtwert: <span className="font-semibold text-blue-600">
                        {quarterGroup.entries.reduce((sum, entry) => sum + entry.vk_total, 0).toFixed(2)} €
                      </span>
                    </div>
                    <div>
                      Punkte: <span className="font-semibold text-emerald-600">
                        {quarterGroup.entries.reduce((sum, entry) => sum + (entry.punkte_earned || 0), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <DownloadWarningModal 
          isOpen={showDownloadWarning}
          onClose={() => {
            setShowDownloadWarning(false);
            setPendingDownload(null);
          }}
          onConfirm={confirmDownload}
          title="Eingangsliste PDF herunterladen"
        />
        
        {/* Interactive Tour */}
        <InteractiveTour 
          manualStart={showTourManual}
          onComplete={() => setShowTourManual(false)}
        />
      </div>
    </div>
  );
}